/* ==========================================================================
   Hero FX — twinkling lens glares + drifting haze for the slideshow hero
   (Creative Stage Lighting).
   A hidden "Hero FX" section placed under the slideshow carries the config;
   this script builds the overlay inside the chosen slide. Glare points are
   "x,y,size" in percent of the IMAGE, so they stay pinned through object-fit
   cover crops, focal points, resizes and the theme's zoom-in motion.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function rand(min, max) { return min + Math.random() * (max - min); }

  function parsePoints(str) {
    return String(str || '')
      .split(/[;\n\r]+/)
      .map(function (chunk) {
        var p = chunk.trim().split(/\s*,\s*/);
        if (p.length < 2) return null;
        var x = parseFloat(p[0]), y = parseFloat(p[1]);
        if (isNaN(x) || isNaN(y)) return null;
        var size = (p[2] || 's').trim().toLowerCase().charAt(0);
        if ('sml'.indexOf(size) === -1) size = 's';
        return { x: x, y: y, size: size };
      })
      .filter(Boolean);
  }

  function parseObjectPosition(value) {
    var parts = String(value || '50% 50%').trim().split(/\s+/);
    var map = { left: 0, center: 50, right: 100, top: 0, bottom: 100 };
    var toPct = function (v, fallback) {
      if (v in map) return map[v];
      if (/%$/.test(v)) return parseFloat(v);
      return fallback;
    };
    return { x: toPct(parts[0] || '50%', 50), y: toPct(parts[1] || parts[0] || '50%', 50) };
  }

  /* Find the slideshow section that sits right above the config's section */
  function findSlideBg(config) {
    var section = config.closest('.shopify-section') || config.parentElement;
    var node = section ? section.previousElementSibling : null;
    var hops = 0;
    while (node && hops < 4) {
      if (node.querySelector && node.querySelector('.slideshow__bg')) break;
      node = node.previousElementSibling;
      hops++;
    }
    if (!node || !node.querySelector) return null;
    var slides = node.querySelectorAll('.swiper-slide');
    var index = Math.max(1, parseInt(config.getAttribute('data-slide'), 10) || 1) - 1;
    var slide = slides[index] || slides[0];
    return slide ? slide.querySelector('.slideshow__bg') : node.querySelector('.slideshow__bg');
  }

  function buildOverlay(config, bg) {
    var old = bg.querySelector('.hero-fx');
    if (old) old.parentNode.removeChild(old);

    var root = document.createElement('div');
    root.className = 'hero-fx';
    root.setAttribute('aria-hidden', 'true');
    root.style.setProperty('--hfx-color', config.getAttribute('data-color') || '255, 241, 214');
    root.style.setProperty('--hfx-twinkle', config.getAttribute('data-twinkle') || '0.7');
    root.style.setProperty('--hfx-smoke', config.getAttribute('data-smoke') || '0.45');
    root.style.setProperty('--hfx-smoke-speed', config.getAttribute('data-smoke-speed') || '1');

    if (parseFloat(config.getAttribute('data-smoke')) > 0) {
      var haze = document.createElement('div');
      haze.className = 'hero-fx__haze';
      ['1', '2'].forEach(function (n) {
        var i = document.createElement('i');
        i.className = 'hero-fx__smoke hero-fx__smoke--' + n;
        haze.appendChild(i);
      });
      root.appendChild(haze);
    }
    var layer = document.createElement('div');
    layer.className = 'hero-fx__sparks';
    root.appendChild(layer);
    bg.appendChild(root); /* after the slide's dark overlay, so glares stay bright */
    return { root: root, layer: layer };
  }

  function HeroFx(config) {
    var bg = findSlideBg(config);
    if (!bg) return;
    this.img = bg.querySelector('img');
    this.points = parsePoints(config.getAttribute('data-points'));
    var built = buildOverlay(config, bg);
    this.root = built.root;
    this.layer = built.layer;
    this.bg = bg;
    if (!this.img || !this.points.length || parseFloat(config.getAttribute('data-twinkle')) <= 0) return;

    this.buildSparks();
    this.place = this.place.bind(this);

    var self = this;
    if (this.img.complete && this.img.naturalWidth) this.place();
    this.img.addEventListener('load', this.place);
    window.addEventListener('resize', this.place);
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(this.place);
      this.ro.observe(bg);
    }
    /* the theme zooms the image during the first moments: follow it, then settle */
    var start = performance.now();
    var follow = function (now) {
      self.place();
      if (now - start < 3500) requestAnimationFrame(follow);
    };
    requestAnimationFrame(follow);
  }

  HeroFx.prototype.buildSparks = function () {
    var frag = document.createDocumentFragment();
    var sizes = { s: [26, 40], m: [40, 60], l: [120, 170] };
    var durs = { s: [1.9, 6.5], m: [2.6, 7], l: [5, 9] };
    this.points.forEach(function (pt, i) {
      var el = document.createElement('span');
      el.className = 'hero-fx__spark hero-fx__spark--' + pt.size;
      var sz = rand(sizes[pt.size][0], sizes[pt.size][1]);
      /* every spark gets its own period and a different starting phase, set as real
         longhands (not just custom properties) so no browser can collapse them */
      var dur = rand(durs[pt.size][0], durs[pt.size][1]);
      var delay = -(rand(0, dur) + i * 0.37);
      el.style.setProperty('--x', pt.x + '%');
      el.style.setProperty('--y', pt.y + '%');
      el.style.setProperty('--sz', sz.toFixed(0) + 'px');
      el.style.setProperty('--dur', dur.toFixed(2) + 's');
      el.style.setProperty('--delay', delay.toFixed(2) + 's');
      el.style.animationDuration = dur.toFixed(2) + 's';
      el.style.animationDelay = delay.toFixed(2) + 's';
      el.style.setProperty('--peak', rand(pt.size === 'l' ? 0.85 : 0.55, 1).toFixed(2));
      el.style.setProperty('--rot', rand(-25, 25).toFixed(0) + 'deg');
      el.style.setProperty('--streak', rand(0.7, 1.4).toFixed(2));
      el.appendChild(document.createElement('i'));
      frag.appendChild(el);
    });
    this.layer.appendChild(frag);
  };

  /* Size the spark layer to the displayed (cover-cropped) image box */
  HeroFx.prototype.place = function () {
    var img = this.img;
    var iw = img.naturalWidth || parseFloat(img.getAttribute('width')) || 0;
    var ih = img.naturalHeight || parseFloat(img.getAttribute('height')) || 0;
    if (!iw || !ih) return;
    var rootRect = this.root.getBoundingClientRect();
    var r = img.getBoundingClientRect();
    if (!r.width || !r.height) return;
    var cs = getComputedStyle(img);
    var fit = cs.objectFit || 'cover';
    var scale = fit === 'contain' ? Math.min(r.width / iw, r.height / ih) : Math.max(r.width / iw, r.height / ih);
    var dw = iw * scale, dh = ih * scale;
    var pos = parseObjectPosition(cs.objectPosition);
    var left = r.left - rootRect.left + (r.width - dw) * pos.x / 100;
    var top = r.top - rootRect.top + (r.height - dh) * pos.y / 100;
    var s = this.layer.style;
    s.left = left.toFixed(1) + 'px';
    s.top = top.toFixed(1) + 'px';
    s.width = dw.toFixed(1) + 'px';
    s.height = dh.toFixed(1) + 'px';
  };

  function init() {
    var configs = document.querySelectorAll('[data-hero-fx-config]');
    Array.prototype.forEach.call(configs, function (config) {
      if (config.__heroFx && config.__heroFx.root && config.__heroFx.root.isConnected) return;
      config.__heroFx = new HeroFx(config);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  /* theme editor: re-attach when either the slideshow or this section re-renders */
  document.addEventListener('shopify:section:load', function () { setTimeout(init, 50); });
  document.addEventListener('shopify:section:reorder', function () { setTimeout(init, 50); });
  window.HeroFxInit = init;
})();
